import React, { Suspense, lazy, useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { trackEvent } from '@/lib/analytics';
import { isMobileDevice } from '@/lib/webChat';

const WebChatPanel = lazy(() => import('@/components/WebChatPanel'));

/**
 * Floating "chat on WhatsApp" button for the public Parcelink hosts (apex,
 * drivers, partners). Configuration lives in recruitment_cms.whatsapp_chat:
 *   { enabled: boolean, number: "4477…" (digits, country code first),
 *     greeting: "prefilled first message", label: "button text" }
 * set from Admin → Marketing → WhatsApp chat. Nothing renders until enabled
 * with a number, so shipping this changes nothing on its own.
 *
 * On a computer, with `web_chat: true` in the same config, the button opens
 * the on-screen chat (WebChatPanel) instead of WhatsApp Web: name and mobile
 * first, a texted code, then a chat that lands in the portal's WhatsApp inbox.
 * A phone or tablet always opens the visitor's own WhatsApp.
 */
const isPublicParcelinkHost = () => {
  const h = (typeof window !== 'undefined' && window.location.hostname) || '';
  if (!/(^|\.)parcelink\.org$/.test(h)) return false;
  return !/^(portal|admin|accounting|receipts|onboarding)\./.test(h);
};

const WhatsAppChatButton = () => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!isPublicParcelinkHost()) return undefined;
    if (typeof navigator !== 'undefined' && navigator.webdriver) return undefined;
    let alive = true;
    supabase
      .from('recruitment_cms')
      .select('value')
      .eq('key', 'whatsapp_chat')
      .maybeSingle()
      .then(({ data }) => { if (alive) setCfg(data?.value || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const number = String(cfg?.number || '').replace(/[^0-9]/g, '');
  if (!cfg?.enabled || !number) return null;
  const label = cfg.label || 'Chat on WhatsApp';
  const href = `https://wa.me/${number}?text=${encodeURIComponent(cfg.greeting || 'Hi Parcelink, I have a question about ')}`;
  const buttonClass = 'fixed bottom-24 right-4 z-[110] flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-2xl ring-2 ring-white/70 transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#25D366]/40 sm:bottom-6 sm:right-6 sm:h-auto sm:w-auto sm:gap-2 sm:px-4 sm:py-3';

  if (cfg.web_chat === true && !isMobileDevice()) {
    return (
      <>
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          onClick={() => {
            if (!open) trackEvent('web_chat_open', { host: window.location.hostname });
            setOpen((o) => !o);
          }}
          className={buttonClass}
        >
          <MessageCircle className="h-6 w-6" aria-hidden="true" />
          <span className="hidden text-sm font-bold sm:inline">{label}</span>
        </button>
        {open && (
          <Suspense fallback={null}>
            <WebChatPanel onClose={() => setOpen(false)} whatsappHref={href} label={label} topics={cfg.topics} />
          </Suspense>
        )}
      </>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      onClick={() => trackEvent('whatsapp_chat_click', { host: window.location.hostname })}
      className={buttonClass}
    >
      <MessageCircle className="h-6 w-6" aria-hidden="true" />
      <span className="hidden text-sm font-bold sm:inline">{label}</span>
    </a>
  );
};

export default WhatsAppChatButton;
